import React, { useState, useEffect, useCallback } from 'react';
import {
  Avatar,
  DialogTitle,
  DialogContent,
  DialogActions,
  Dialog,
  Button,
  Tooltip,
} from '@material-ui/core';
import ReactSVG from 'react-svg';
import { Edit, Delete, Textsms } from '@material-ui/icons';
import * as lib from '~/dependencies';
import {
  useContactInfo,
  useDeleteContact,
  useTryToCall,
  useNavigation,
} from '~/hooks';
import { validateNumberType, formatInternationalE164 } from '~/helpers/format';
import * as NuacomComponent from '~/components';
import * as Loaders from '~/components/Loaders';
import { CallIcon, IconEmail } from '~/assets/svgIcons';
import {
  Container,
  BackButton,
  ButtonField,
  DisplayTop,
  BoxBtn,
  BoxInformations,
  Chip,
} from './styles';

export default function ContactDetails(props) {
  const { location, match, history } = props;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // HOOKS
  const [contactInfo, setContactInfo] = useContactInfo();
  const [deleteContact, setDeleteContact] = useDeleteContact();
  const tryToCall = useTryToCall();
  const navigate = useNavigation();
  const { t } = lib.useTranslation();

  const { tab } = location.state;

  const goBack = useCallback(() => {
    history.push({
      pathname: `${match.path}/contacts`,
      state: {
        tab: location.state.tab || 0,
        // Contains the last contact details accessed
        lastContactDetailsIndex: location.state.contactItemIndex,
      },
    });
  }, [
    history,
    location.state.tab,
    location.state.contactItemIndex,
    match.path,
  ]);

  useEffect(() => {
    setContactInfo(
      location.pathname.split('/app/contactDetails/').join('') ||
        location.state.id
    );
  }, [location, setContactInfo]);

  useEffect(() => {
    if (deleteContact) {
      lib.ToastMessage('success', 'Contact Deleted.');
      goBack();
    }
  }, [deleteContact, goBack]);

  useEffect(() => {
    if (contactInfo.id) {
      setLoading(false);
    }
  }, [contactInfo]);

  const { organization } = contactInfo;

  return (
    <>
      <NuacomComponent.Header {...props}>
        <lib.Link
          to={{
            pathname: `${match.path}/addNewContact`,
            state: { edit: true, id: contactInfo.id, tab },
            hash: '#edit',
          }}
          data-test-id="edit-contact-icon">
          <BoxBtn>
            <Edit className="icon" />
          </BoxBtn>
        </lib.Link>

        <BoxBtn
          onClick={() => setOpen(true)}
          style={{ marginRight: 15 }}
          data-test-id="delete-contact-icon">
          <Delete className="icon" />
        </BoxBtn>

        <BackButton fullWidth onClick={goBack}>
          Cancel
        </BackButton>
      </NuacomComponent.Header>

      <Container>
        <DisplayTop />

        <div id="box-avatar">
          <Avatar className="avatar">
            <span>
              {loading && <Loaders.Avatar size="94" />}
              {!loading && contactInfo && contactInfo.first_name
                ? contactInfo.first_name.slice(0, 2).toUpperCase()
                : ''}
            </span>
          </Avatar>
          {!loading ? (
            <span className="title">
              {`${contactInfo && contactInfo.first_name} `}
              {`${contactInfo &&
                contactInfo.last_name &&
                contactInfo.last_name}`}
            </span>
          ) : (
            <span className="title">
              <Loaders.Line size="140" />
            </span>
          )}
        </div>

        <Dialog
          disableEscapeKeyDown
          maxWidth="xs"
          aria-labelledby="confirmation-dialog-title"
          open={open}>
          <DialogTitle id="confirmation-dialog-title">Confirmation</DialogTitle>
          <DialogContent dividers>
            This contact will be deleted. Press OK to confirm.
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() =>
                setDeleteContact(contactInfo.id, tab === 0 ? 'Personal' : 'All')
              }
              color="primary">
              Ok
            </Button>
          </DialogActions>
        </Dialog>

        <BoxInformations>
          {contactInfo.landline && (
            <div className="row">
              <div className="left">
                <span>Landline</span>
                <span>{formatInternationalE164(contactInfo.landline)}</span>
              </div>
              <div className="right">
                <ButtonField
                  onClick={() =>
                    tryToCall(contactInfo.landline, {
                      name: `${contactInfo.first_name} ${contactInfo.last_name}`,
                      contactId: contactInfo.id,
                    })
                  }>
                  <ReactSVG src={CallIcon} className="call-icon" />
                </ButtonField>
              </div>
            </div>
          )}
          {contactInfo.mobile && (
            <div className="row">
              <div className="left">
                <span>Mobile</span>
                <span>{formatInternationalE164(contactInfo.mobile)}</span>
              </div>
              {validateNumberType(contactInfo.mobile) === 'MOBILE' && (
                <div className="right icon-sms">
                  <Tooltip title={t('screens.contactDetails.sendSMS')}>
                    <ButtonField
                      onClick={() =>
                        navigate.smsSenderView({
                          name: `${contactInfo.first_name} ${contactInfo.last_name}`,
                          mobile: contactInfo.mobile,
                        })
                      }>
                      <Textsms className="icon-sms-svg" />
                    </ButtonField>
                  </Tooltip>
                </div>
              )}
              <div className="right">
                <ButtonField
                  onClick={() =>
                    tryToCall(contactInfo.mobile, {
                      name: `${contactInfo.first_name} ${contactInfo.last_name}`,
                      contactId: contactInfo.id,
                    })
                  }>
                  <ReactSVG src={CallIcon} className="call-icon" />
                </ButtonField>
              </div>
            </div>
          )}
          {contactInfo.email && (
            <div className="row">
              <div className="left">
                <span>E-mail</span>
                <span>{contactInfo.email}</span>
              </div>
              <div className="right">
                <a href={`mailto:${contactInfo.email}`}>
                  <ButtonField>
                    <ReactSVG src={IconEmail} className="call-icon" />
                  </ButtonField>
                </a>
              </div>
            </div>
          )}
          {Array.isArray(organization) && organization?.length ? (
            <div className="row">
              <div className="left">
                <span>Organization</span>
                <span>{organization[0].name}</span>
              </div>
            </div>
          ) : null}
          {contactInfo.groups && contactInfo.groups.length > 0 && (
            <div className="row">
              <div className="left">
                <span>Groups</span>
                <Chip>
                  {contactInfo.groups.map(item => (
                    <span key={item.group_id}>{item.group_name}</span>
                  ))}
                </Chip>
              </div>
            </div>
          )}
        </BoxInformations>
      </Container>
    </>
  );
}

ContactDetails.defaultProps = {
  location: [],
  history: [],
  match: [],
};

ContactDetails.propTypes = {
  location: lib.PropTypes.instanceOf(Object),
  history: lib.PropTypes.instanceOf(Object),
  match: lib.PropTypes.instanceOf(Object),
};
